import { CommonModule } from "@angular/common";
import { Component, Inject, OnInit } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatTooltipModule } from "@angular/material/tooltip";
import { HandlerMessageService } from "app/common/services/handler-error/handler-message.service";
import { DeleteConfirmationComponent } from "../../../business-partners/business-partner/delete-modal/delete-modal.component";
import { CompanySubApplicationService } from "./company-sub-application.service";
import { OPERATING_SYSTEM_ICONS, OPERATING_SYSTEM_LABELS } from "./operating-system.constants";
import CompanySubApplicationAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/configurations/company/company-sub-application/company-sub-application.entity";
import { VersionFormModalData } from "./interfaces/company-sub-application.interface";
import { VersionFormModalComponent } from "./version-form-modal/version-form-modal.component";


@Component({
  selector: "app-company-version-config",
  templateUrl: "./company-version-config.component.html",
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
})
export class CompanyVersionConfigComponent implements OnInit {
  versions: CompanySubApplicationAttributes[] = [];
  loading = false;
  readonly platformIcons = OPERATING_SYSTEM_ICONS;

  constructor(
    public dialogRef: MatDialogRef<CompanyVersionConfigComponent>,
    private dialog: MatDialog,
    private companySubAppService: CompanySubApplicationService,
    private handlerMessage: HandlerMessageService,
    @Inject(MAT_DIALOG_DATA) public data: { companyId: string }
  ) {}

  ngOnInit(): void {
    this.loadVersions();
  }

  loadVersions(): void {
    this.loading = true;
    this.companySubAppService.findByCompanyId(this.data.companyId).subscribe({
      next: (res) => {
        const data = res?.data ?? [];
        this.versions = Array.isArray(data) ? (data as CompanySubApplicationAttributes[]) : [];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.handlerMessage.error({
          alternativeMessage: "Error al cargar las versiones configuradas.",
        });
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  addVersion(): void {
    const modalData: VersionFormModalData = {
      companyId: this.data.companyId,
      version: null,
    };
    this.dialog
      .open(VersionFormModalComponent, {
        width: "95vw",
        maxWidth: "800px",
        data: modalData,
        disableClose: true,
      })
      .afterClosed()
      .subscribe((result: CompanySubApplicationAttributes | undefined) => {
        if (result) {
          this.loadVersions();
        }
      });
  }

  editVersion(version: CompanySubApplicationAttributes): void {
    const modalData: VersionFormModalData = {
      companyId: this.data.companyId,
      version: { ...version },
    };
    this.dialog
      .open(VersionFormModalComponent, {
        width: "95vw",
        maxWidth: "800px",
        data: modalData,
        disableClose: true,
      })
      .afterClosed()
      .subscribe((result: CompanySubApplicationAttributes | undefined) => {
        if (result) {
          this.loadVersions();
        }
      });
  }

  deleteVersion(version: CompanySubApplicationAttributes): void {
    this.dialog
      .open(DeleteConfirmationComponent, {
        data: {
          message: `¿Está seguro que desea eliminar la aplicación "${version.name}"?`,
        },
        panelClass: "delete-modal",
        disableClose: true,
      })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (confirmed && version._id) {
          this.companySubAppService
            .deleteEmbedded1d(this.data.companyId, String(version._id))
            .subscribe({
              next: () => {
                this.handlerMessage.success({
                  message: "Aplicación eliminada correctamente.",
                });
                this.loadVersions();
              },
              error: (err) => {
                this.handlerMessage.error({
                  message: err?.error?.message,
                  alternativeMessage: "Error al eliminar la aplicación.",
                  statusCode: err?.status,
                });
              },
            });
        }
      });
  }

  getPlatforms(version: CompanySubApplicationAttributes): { icon: string; platform: string; version: string }[] {
    return (version.appVersions ?? []).map((av) => ({
      icon: OPERATING_SYSTEM_ICONS[av.operatingSystem] ?? "computer",
      platform: OPERATING_SYSTEM_LABELS[av.operatingSystem] ?? av.operatingSystem,
      version: av.version,
    }));
  }
}
